/**
 * Post-processing the engine's raw `graph` output goes through before anyone reads it: compressed edges are
 * expanded through `value.instancePaths`, unhydrated debug instances get their template's fields, and
 * path/index/parent_path are derived from `v` when asked. (Moved from services/decisionapi/Turbo.ts.)
 */

// Same as imminently_fn's removeUndefinedFields: drops undefined keys, recursing into (and mutating) objects.
const removeUndefinedFields = (obj: any) => {
  for (const key of Object.keys(obj)) {
    const keyVal = obj[key];
    if (keyVal && typeof keyVal === "object") {
      removeUndefinedFields(keyVal);
    } else if (keyVal === undefined) {
      delete obj[key];
    }
  }
  return obj;
};

export const rehydrateGraph = (
  rawGraph: any,
  options: { derivePathing?: boolean } = {},
): any => {
  if (!rawGraph || typeof rawGraph !== "object") return rawGraph;
  const shouldHydrateDebugInstances =
    rawGraph.value?.debugInstancesUnhydrated === true;
  const shouldDerivePathing = options.derivePathing === true;
  const shouldExpandEdges =
    rawGraph.value?.noEdges !== true &&
    Array.isArray(rawGraph.edges) &&
    rawGraph.edges.length > 0 &&
    rawGraph.value?.instancePaths;
  if (
    !shouldExpandEdges &&
    (!Array.isArray(rawGraph.nodes) ||
      (!shouldHydrateDebugInstances && !shouldDerivePathing))
  ) {
    return rawGraph;
  }

  let expandedEdges = rawGraph.edges;
  if (shouldExpandEdges) {
    const resolvedInstancePaths = rawGraph.value.instancePaths;
    const seen = new Set<string>();
    const nextEdges: any[] = [];
    const addEdge = (edge: any) => {
      const normalizedEdge =
        edge?.v && edge?.w ? { v: edge.v, w: edge.w } : edge;
      const key = `${normalizedEdge?.v ?? ""}->${normalizedEdge?.w ?? ""}`;
      if (seen.has(key)) return;
      seen.add(key);
      nextEdges.push(normalizedEdge);
    };

    for (const edge of rawGraph.edges) {
      addEdge(edge);
      const instances = edge?.value?.instances;
      if (!instances || typeof instances !== "object") continue;
      for (const [instancePath, targetInstances] of Object.entries(instances)) {
        const fromPath =
          instancePath === "global"
            ? "global"
            : resolvedInstancePaths[instancePath as any];
        const targets = Array.isArray(targetInstances) ? targetInstances : [];
        for (const targetInstance of targets) {
          const toPath =
            targetInstance === "global"
              ? "global"
              : resolvedInstancePaths[targetInstance as any];
          if (
            (instancePath !== "global" && !fromPath) ||
            (targetInstance !== "global" && !toPath)
          ) {
            continue;
          }
          addEdge({
            v: instancePath === "global" ? edge.v : `${fromPath}/${edge.v}`,
            w: targetInstance === "global" ? edge.w : `${toPath}/${edge.w}`,
          });
        }
      }
    }
    expandedEdges = nextEdges;
  }

  if (!Array.isArray(rawGraph.nodes)) {
    return expandedEdges === rawGraph.edges
      ? rawGraph
      : { ...rawGraph, edges: expandedEdges };
  }

  const hiddenTemplateByGuid = new Map<string, any>();
  for (const node of rawGraph.nodes) {
    if (!node.v.includes("/")) {
      hiddenTemplateByGuid.set(node.v, node.value);
    }
  }

  if (hiddenTemplateByGuid.size === 0 && !shouldDerivePathing) {
    return expandedEdges === rawGraph.edges
      ? rawGraph
      : { ...rawGraph, edges: expandedEdges };
  }

  const nodes = rawGraph.nodes.map((node: any) => {
    if (!node.v.includes("/")) {
      return node;
    }
    const guid = node.v.split("/").pop();
    if (!guid) return node;
    const template = hiddenTemplateByGuid.get(guid);
    if (!template && !shouldDerivePathing) return node;
    const pathParts = node.v.split("/");
    const attributePathParts = pathParts.slice(0, -1);
    const parentPath =
      attributePathParts.length > 2
        ? attributePathParts.slice(0, -2).join("/") || undefined
        : undefined;
    const instanceIndex =
      pathParts.length >= 3 ? pathParts[pathParts.length - 2] : undefined;
    return {
      ...node,
      value: removeUndefinedFields({
        ...(shouldHydrateDebugInstances ? template : undefined),
        ...node.value,
        id: shouldHydrateDebugInstances ? guid : node.value.id,
        hidden: shouldHydrateDebugInstances
          ? (node.value.hidden ?? false)
          : node.value.hidden,
        index: options.derivePathing
          ? (node.value.index ?? template?.index ?? instanceIndex)
          : node.value.index,
        path: options.derivePathing
          ? (node.value.path ?? node.v)
          : node.value.path,
        parent_path: options.derivePathing
          ? (node.value.parent_path ?? parentPath)
          : node.value.parent_path,
      }),
    };
  });
  return { ...rawGraph, nodes, edges: expandedEdges };
};
